import { themedUseStyletron } from '../../../theme/customTheme';
import { ValueDisplayWithLabel } from '../../components/ValueDisplay';
import { useSetObjectValue } from '../../../hooks/useSetObjectValue';
import { OperatorPanelWrapper } from '../../components/Layout';
import { FlexDate, FlexDateDto, FlexDateRange, ICalendar } from './types';
import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Block } from 'baseui/block';
import { ButtonGroup, MODE, SIZE } from 'baseui/button-group';
import { Button, KIND, SHAPE } from 'baseui/button';
import { VscAdd, VscChevronLeft, VscChevronRight, VscListOrdered, VscRefresh, VscTrash } from 'react-icons/vsc';
import { LabelLarge } from 'baseui/typography';
import { convert, convertBack } from './converter';
import { dateRange_intersects, date_intersects, date_isFixDate, hasEntries, toFlexDateDto, weekNDay_intersects } from './utils';
import { IoCalendarNumberOutline } from 'react-icons/io5';
import * as config from './config'
import { AddEventDialog, IAddEventState } from './AddEventDialog/AddEventDialog';
import { getHolidays, isSupported } from './publicHolidays';
import { useIntl, FormattedMessage, FormattedDate } from 'react-intl';
import useIsMobile from '../../../hooks/useIsMobile/useIsMobile';
import { Notification } from "baseui/notification";
import { addDays } from 'date-fns';

const CalendarPanel = React.lazy(() => import('./CalendarPanel'));

const OperatorPanel = (props: ICalendar) => {
    const [css, theme] = themedUseStyletron();
    const intl = useIntl();
    const [dates, setDates] = useSetObjectValue(props.id, props.resourceGroupId, "dates", props.dates, 100);
    const [dateRanges, setDateRanges] = useSetObjectValue(props.id, props.resourceGroupId, "dateRanges", props.dateRanges, 100);
    const [weekNDays, setWeekNDays] = useSetObjectValue(props.id, props.resourceGroupId, "weekNDays", props.weekNDays, 100);
    const calendarRef:any = useRef();
    const [events, setEvents] = useState<any[]>([]);
    const [selected, setSelected] = useState<number>(0);
    const isMobile = useIsMobile();
    const [view, setView] = useState(isMobile ? 'listWeek' : 'dayGridMonth');
    const [changed, setChanged] = useState(false);
    const [displayedDates, setDisplayedDates] = useState({ start: new Date(), end: new Date() });
    const [dateListT, setDateListT] = useState<any>();


    useEffect(() => {
        setDateListT(convert(props))
    }, [props.dates, props.dateRanges, props.weekNDays]);


    useEffect(() => {
        if (isMobile)
            updateView('listWeek')
        else
            updateView('dayGridMonth')
    }, [isMobile]);

    useEffect(() => {
        const events: any[] = [];
        var date = displayedDates.start;
        var drs = dateListT?.dateRanges;
        var wnds = dateListT?.weekNDays;
        let idCounter = 0;
        while (date < displayedDates.end) {
            const d = date;
            const dateIners = dateListT?.dates.filter((entry) => date_intersects(entry, d));
            dateIners?.map((entry: any, index: number) => events.push({
                id: `${idCounter++}`, allDay: true, title: 'DAY', date: d, mySource: entry, myType: 'FlexDateDto',
                borderColor: theme.colors.accent100, backgroundColor: theme.colors.accent50, textColor: theme.colors.notificationInfoText
            }));

            const dateRangeIners = drs?.filter((entry) => dateRange_intersects(entry, d));
            dateRangeIners?.map((entry: any, index: number) => events.push({
                id: `${idCounter++}`, allDay: true, title: 'RANGE', date: d, mySource: entry, myType: 'FlexDateRangeDto',
                borderColor: theme.colors.positive100, backgroundColor: theme.colors.positive50, textColor: theme.colors.notificationPositiveText
            }));

            const wndayIners = wnds?.filter((entry) => weekNDay_intersects(entry, d));
            wndayIners?.map((entry: any, index: number) => events.push({
                id: `${idCounter++}`, allDay: true, title: 'WND', date: d, mySource: entry, myType: 'FlexWeekNDayDto',
                borderColor: theme.colors.warning100, backgroundColor: theme.colors.warning50, textColor: theme.colors.notificationWarningText
            }));

            date = addDays(date, 1);
        }
        if (events && events.length > 0)
            setEvents(events);
        else
            setEvents([]);

    }, [dateListT, displayedDates]);

    const fg = props.isOn ? theme.appColors.aggregate.on : theme.appColors.aggregate.off;
    const listView = view === 'listWeek';
    const writing = setDates.isLoading || setDateRanges.isLoading || setWeekNDays.isLoading;

    const noEntries = useMemo(() => !hasEntries(props),[props]);

    const isAddPublicHolidaysSupported = isSupported('at')
    return (
        <>
            <OperatorPanelWrapper
                objectId={props.id}
                resourceGroupId={props.resourceGroupId}
                statusContent={
                    <>
                        <ValueDisplayWithLabel color={fg} label={intl.formatMessage({ id: "Calendar.Current", defaultMessage: "currently" })} small value={props.isOn} dimension=""></ValueDisplayWithLabel>
                    </>
                }
                operationModeContent={
                    <></>
                }
                additionalContent={
                    <></>
                }>
            </OperatorPanelWrapper>


            <Block maxWidth='600px' height='100%' width='95%' marginTop={theme.sizing.scale800} display='flex' flexDirection='column' gridGap={theme.sizing.scale800} >
                {noEntries && <Notification kind={'warning'}>{() => <FormattedMessage id='Calendar.noEntriesNotification' defaultMessage={'This calendar has no entries.'}></FormattedMessage>}</Notification>}
                {changed && <Button size='compact' isLoading={writing} disabled={!changed} onClick={onApplyChanges}><FormattedMessage id="applyChanges" defaultMessage="Apply changes" /></Button>}
                <Block width='100%' display='flex' flexDirection='row' gridGap={theme.sizing.scale500} justifyContent='space-between' alignItems='baseline'>
                    <Block display='flex' flexDirection='row' gridGap={theme.sizing.scale200} >
                        <Button kind='secondary' size={SIZE.compact} onClick={setToday}><FormattedMessage id="today" defaultMessage="Today" /></Button>
                        <ButtonGroup size={SIZE.compact} mode={MODE.radio} >
                            <Button onClick={prev}><VscChevronLeft /></Button>
                            <Button onClick={next}><VscChevronRight /></Button>
                        </ButtonGroup>
                    </Block>

                    {!listView &&

                        <LabelLarge><FormattedDate value={addDays(displayedDates.start, 8)}
                            year="numeric"
                            month="long"></FormattedDate>
                        </LabelLarge>
                    }
                    {listView &&
                        <>
                            <LabelLarge>
                                <FormattedDate value={displayedDates.start}
                                    year="numeric"
                                    month="long" day="numeric"></FormattedDate>
                                <span> - </span>
                                <FormattedDate value={displayedDates.end}
                                    year="numeric"
                                    month="long" day="numeric"></FormattedDate>
                            </LabelLarge>
                        </>
                    }
                        <ButtonGroup kind='tertiary' size={SIZE.compact} mode={MODE.radio} selected={selected}>
                            <Button kind='tertiary' onClick={() => changeView('dayGridMonth', 0)} title='show Month'><IoCalendarNumberOutline /></Button>
                            <Button kind='tertiary' onClick={() => changeView('listWeek', 1)} title='show List'><VscListOrdered /></Button>
                        </ButtonGroup>
                </Block>
                <div className={css({
                    position: 'relative',

                })}>
                    <Block>
                        <Suspense fallback={"loading ..."}>
                            <CalendarPanel ref={calendarRef} businessHours={config.businessHours} datesSet={datesSet} renderEventContent={renderEventContent}
                                events={events} view={view}></CalendarPanel>
                        </Suspense>
                        <div className={css({
                            position: 'absolute',
                            bottom: '-20px',
                            right: '-20px',
                            zIndex: 9999,
                        })}>
                            <AddEvent alignSelf='flex-end' onAdd={onAddEvent}></AddEvent>
                        </div>
                    </Block>
                </div>
                <Block>
                    { isAddPublicHolidaysSupported && <Button onClick={onAddPublicHolidays} kind='secondary' size='compact'><FormattedMessage id="Calendar.AddPublicHolidays" defaultMessage="Add public Holidays" /></Button>}
                    <Button onClick={onClearAll} kind='tertiary' size='compact'><FormattedMessage id="Calendar.ClearAll" defaultMessage="Clear all" /></Button>
                </Block>
            </Block>
        </>
    )

    function updateView(view: string) { // index: number
        setView(view);
    }

    function onClearAll() {
        setDateListT((prevState) => {
            return {
                ...prevState,
                dates: [],
                dateRanges: []
            }
        })
        setChanged(true);
    }

    function onAddPublicHolidays() {
        addEvents(getHolidays('at'));
    }


    function addEvents(events: any[]) {
        const dates = events?.filter((entry) => (entry as FlexDateDto) !== undefined);

        setDateListT((prevState) => {
            const tmp = [...prevState.dates];
            dates?.map((item) => tmp.push(item));
            return {
                ...prevState,
                dates: tmp,
            }
        })
        setChanged(true);
    }

    function onAddEvent(event: IAddEventState) {

        // flex date added
        if (event.date) {
            const newDate = event.date;
            setDateListT((prevState) => {
                const tmp = [...prevState.dates];
                tmp.push(toFlexDateDto(newDate));
                return {
                    ...prevState,
                    dates: tmp
                }
            })
        }
        // flex date range added
        else if (event.dateRange) {
            const newStart = event.dateRange.start;
            const newEnd = event.dateRange.end;
            setDateListT((prevState) => {
                const tmp = [...prevState.dateRanges];
                tmp.push({ start: toFlexDateDto(newStart), end: toFlexDateDto(newEnd) });
                return {
                    ...prevState,
                    dateRanges: tmp
                }
            })
        }
        setChanged(true);
    }

    function onApplyChanges() {
        const res = convertBack(dateListT);
        setDates(res.dates);
        setDateRanges(res.dateRanges);
        setWeekNDays(res.weekNDays);
        setChanged(false);
    }

    function changeView(view: string, index: number) {
        setView(view)
        setSelected(index);
    }

    function datesSet(args: any) {
        if (displayedDates.start.getTime() !== args.start.getTime() || displayedDates.end.getTime() !== args.end.getTime()) {
            setDisplayedDates({ start: args.start, end: args.end });
        }
    }
    function setToday() {
        calendarRef?.current?.setToday();
    }

    function prev() {
        calendarRef?.current?.prev();
    }
    function next() {
        calendarRef?.current?.next();
    }

    function onDeleteEvent(event: any) {
        const source = event.extendedProps.mySource;
        const type = event.extendedProps.myType;
        if (type === 'FlexDateDto') {
            const index = dateListT?.dates.findIndex((ev) => ev === source);
            if (index !== undefined) {
                setDateListT((prevState) => {
                    const tmp = [...prevState.dates];
                    tmp.splice(index, 1);
                    return {
                        ...prevState,
                        dates: tmp
                    }
                })
            }
            setChanged(true);
        }
        else if (type === 'FlexDateRangeDto') {
            const index = dateListT?.dateRanges.findIndex((ev) => ev === source);
            if (index !== undefined) {
                setDateListT((prevState) => {
                    const tmp = [...prevState.dateRanges];
                    tmp.splice(index, 1);
                    return {
                        ...prevState,
                        dateRanges: tmp
                    }
                })
            }
            setChanged(true);
        }
    }

    function renderEventContent(eventInfo: any) {
        const listView = view === 'listWeek';
        const source = eventInfo.event.extendedProps.mySource;
        const fixedDate = date_isFixDate(source);
        return (
            < >
                {listView && <>
                    <Block display='flex' flexDirection='row' justifyContent='space-between' alignItems='baseline'>
                        {!fixedDate &&
                            <Block display='flex' flexDirection='row' gridGap={theme.sizing.scale200}><VscRefresh />
                                <FormattedMessage id="Calendar.RecurringEvent" defaultMessage="recurring Event" />
                            </Block>}
                        <Button kind='tertiary' size={SIZE.compact} shape={SHAPE.round} onClick={() => onDeleteEvent(eventInfo.event)}><VscTrash /></Button>
                    </Block>
                </>}
                {!listView && <>
                    <Block marginLeft={theme.sizing.scale100} display='flex' flexDirection='row' justifyContent='space-between' alignItems='baseline'>
                        {!fixedDate && <VscRefresh color={eventInfo.textColor} />}
                        <Button kind='tertiary' size={SIZE.mini} shape={SHAPE.round} onClick={() => onDeleteEvent(eventInfo.event)}><VscTrash color={eventInfo.textColor} /></Button>
                    </Block>
                </>}

            </>
        )
    };



}

export default OperatorPanel;


function AddEvent(props: any) {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <>
            <Button onClick={() => setIsOpen(true)} kind={KIND.primary} size={SIZE.default} shape={SHAPE.round} overrides={{
                BaseButton: {
                    style: ({ $theme }) => ({
                        boxShadow: $theme.lighting.shadow400,
                    })
                }
            }}
            >
                <VscAdd></VscAdd>
            </Button>
            <AddEventDialog onAdd={props.onAdd} isOpen={isOpen} setIsOpen={setIsOpen} />
        </>
    )

};